<template>
  <div id="SectionSBTiFAQs">
    <UIContainer class="SectionSBTiFAQs__container">
      <template #left>
        <SectionTitle>
          <template #title>FAQs</template>
          <template #description>
            Have more questions about SBTi? Can’t find the answer you’re looking
            for? Please
            <UIButtonText
              class="SectionSBTiFAQs__support-link"
              :to="{ name: 'Contact' }"
            >
              contact our support team.
            </UIButtonText>
          </template>
        </SectionTitle>
      </template>

      <template #right>
        <UIFaqs :faqs="faqs" renderFormat="markdown" />
      </template>
    </UIContainer>
  </div>
</template>

<script setup lang="ts">
import SectionTitle from '@/views/components/misc/SectionTitle.vue'
import UIButtonText from '@/views/components/ui/buttons/UIButtonText.vue'
import UIContainer from '@/views/components/ui/containers/UIContainer/UIContainer.vue'
import UIFaqs from '@/views/components/features/faqs/UIFaqs.vue'

const faqs = [
  {
    question: "What are science-based targets' objectives?",
    answer: `Science-based targets (SBTs) align corporate decarbonisation with a 1.5°C pathway under the Paris Agreement.\n\n- Connect actions to the Paris Agreement and credible net-zero pathways.\n- Move from intent to measurable, time-bound GHG reductions.\n- Accelerate value-chain decarbonisation and long-term resilience.`,
  },
  {
    question: 'What are science-based targets and how can my company use them?',
    answer: `SBTs are a method to strategically reduce greenhouse gas emissions across Scopes 1, 2, and 3. By assessing your emissions data, you establish benchmarks and time-bound targets for required reductions.\n\nThe SBTi sets criteria and sector guidance to align with the Paris Agreement and supports ESRS/CSRD-ready disclosure.`,
  },
  {
    question: 'Should companies submit targets to the SBTi?',
    answer: `Submitting to the SBTi is optional but strategic. The process requires robust data, internal resourcing, and time.\n\nExpert guidance can streamline scoping, data preparation, method selection, and submission.`,
  },
  {
    question: 'What are SBTi target validation criteria?',
    answer: `Use the most recent inventory year as the baseline; earlier baselines may be accepted if latest-year data are included.\n\n- Targets span 5–10 years from submission; baseline year no earlier than 2015.\n- 2023 submissions: typical achievement window 2027–2033; include data up to 2021.\n- Cover Scopes 1, 2, and 3 where applicable using SBTi-approved methods and sector guidance.`,
  },
  {
    question: 'What are intensity-based targets?',
    answer: `Intensity-based targets measure emissions per unit of activity (e.g., per product or per revenue). They are acceptable if they drive absolute reductions or follow approved sector pathways.`,
  },
  {
    question: 'How do peak emission targets fit into the SBTi landscape?',
    answer: `Peak emission targets allow emissions to rise until a defined year before declining; they are not accepted for SBTi validation.`,
  },
  {
    question: 'Do SBTi guidelines vary based on business type or sector?',
    answer: `Yes. SMEs have a simplified route; larger companies follow a multi-step process. Minimum ambition typically requires about 4.2% linear annual reduction for Scopes 1–2 and about 2.5% for Scope 3. If Scope 3 exceeds 40% of total emissions, a Scope 3 target is required. The Sectoral Decarbonization Approach (SDA) provides sector-specific pathways.`,
  },
  {
    question: 'How can organisations benchmark their commitments?',
    answer: `Benchmarking against peers helps calibrate ambition. Public resources like SBTi’s “Companies Taking Action” provide useful reference points.`,
  },
]
</script>

<style lang="scss" scoped>
#SectionSBTiFAQs {
  .SectionSBTiFAQs__container {
    background-color: $color-white;
    :deep(.UIContainer__grid) {
      display: flex;
      flex-direction: column;
      align-items: stretch;

      @include md {
        flex-direction: row;
        align-items: normal;
      }

      .UIContainer__left {
        width: 100%;
        @include md {
          width: 40%;
          padding-right: 3rem;
        }
      }
      .UIContainer__right {
        width: 100%;
        margin-top: 1rem;
        @include md {
          width: 60%;
          margin-top: 0;
        }
      }
    }
  }

  .SectionSBTiFAQs__support-link {
    @include font-size-18px();
    color: $color-ui-gray-cool-600;
    text-decoration: underline;
    cursor: pointer;
  }
}
</style>
